import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { useModal } from '../../../contexts/ModalContext';

const principles = [
  {
    icon: 'ri-focus-3-line',
    title: 'Design Philosophy',
    description:
      'We believe architecture should serve life — not the other way around. Clean, purposeful design that resists trend and stands the test of time is our standard on every project.',
  },
  {
    icon: 'ri-team-line',
    title: 'Client Collaboration',
    description:
      'Every project is a genuine partnership. We take time to understand your vision, lifestyle, and goals before a single line is drawn — then we design together from that foundation.',
  },
  {
    icon: 'ri-medal-2-line',
    title: 'Detail-Driven Execution',
    description:
      'From structural decisions to material selections, we bring rigorous attention to every element of the design and documentation process — no shortcuts, no compromises.',
  },
];

export default function AboutContent() {
  const leftRef = useScrollReveal();
  const rightRef = useScrollReveal();

  return (
    <section className="bg-soft-white">
      {/* Main two-column story block */}
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[700px]">
        {/* Image column */}
        <div ref={leftRef} className="reveal-left relative min-h-[500px] lg:min-h-0 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-top hover:scale-[1.03] transition-transform duration-1000 ease-out"
            style={{
              backgroundImage:
                'url(https://public.readdy.ai/ai/img_res/5c09d27b87c01f3cbbea86b6bb47b473.jpg)',
            }}
          />
          {/* Floating badge */}
          <div className="absolute bottom-8 right-8 bg-dark-ink/90 backdrop-blur-sm p-6 flex flex-col items-center">
            <span className="font-serif text-4xl font-light text-white">20+</span>
            <span className="font-sans text-[9px] tracking-[0.22em] uppercase text-white/45 mt-1">Years of Design</span>
          </div>
        </div>

        {/* Text column */}
        <div ref={rightRef} className="reveal-right flex flex-col justify-center px-10 lg:px-16 xl:px-20 py-20 lg:py-28">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-bronze" />
            <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze">Our Story</p>
          </div>

          <h2 className="font-serif text-5xl lg:text-6xl font-light text-dark-ink mb-8 leading-[1.05]">
            Architecture Rooted in How People Live
          </h2>

          <div className="space-y-5 mb-10">
            <p className="font-sans text-sm text-warm-gray leading-[1.85]">
              Talo Architect was founded with a singular belief: that a great home begins not with aesthetics, but with a deep understanding of how a family lives — their daily patterns, their aspirations, their relationship to light and space.
            </p>
            <p className="font-sans text-sm text-warm-gray leading-[1.85]">
              Since 2002, our Scarsdale-based studio has been designing residential architecture across New York City, Westchester, and White Plains with a commitment to precision, livability, and timeless design. Every project is treated as a collaboration — we listen first, then design.
            </p>
            <p className="font-sans text-sm text-warm-gray leading-[1.85]">
              Our work spans custom homes, thoughtful renovations, and considered additions. What unites them is an attention to detail that goes beyond the drawing — into the quality of light, the feel of materials, and the quiet dignity of well-proportioned space.
            </p>
          </div>

          <CtaButton />
        </div>
      </div>

      {/* Principles grid */}
      <div className="bg-dark-ink py-36 px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <PrinciplesGrid />
        </div>
      </div>

      {/* Studio facts */}
      <StudioFacts />
    </section>
  );
}

function CtaButton() {
  const { openModal } = useModal();
  return (
    <button
      onClick={openModal}
      className="self-start font-sans text-[10px] tracking-[0.2em] uppercase px-10 py-4 bg-dark-ink text-soft-white hover:bg-wood transition-colors duration-300 cursor-pointer whitespace-nowrap"
    >
      Work With Us
    </button>
  );
}

function PrinciplesGrid() {
  const ref = useScrollReveal();
  return (
    <div>
      <div ref={ref} className="reveal-on-scroll mb-16">
        <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-4">Our Principles</p>
        <h3 className="font-serif text-4xl lg:text-5xl font-light text-white">
          Three principles. Every project.
        </h3>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 border-l border-t border-white/10">
        {principles.map((p, i) => (
          <PrincipleCard key={i} principle={p} last={i === principles.length - 1} delay={i + 1} />
        ))}
      </div>
    </div>
  );
}

function PrincipleCard({
  principle,
  last,
  delay,
}: {
  principle: (typeof principles)[0];
  last: boolean;
  delay: number;
}) {
  const ref = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`reveal-on-scroll reveal-delay-${delay} group p-10 border-r border-b border-white/10 hover:bg-white/[0.04] transition-colors duration-300`}
    >
      <div className="w-10 h-10 flex items-center justify-center mb-8">
        <i className={`${principle.icon} text-bronze text-2xl group-hover:text-white/80 transition-colors duration-300`} />
      </div>
      <h4 className="font-serif text-xl font-light text-white mb-4 group-hover:text-bronze/80 transition-colors duration-300">{principle.title}</h4>
      <p className="font-sans text-sm text-white/40 leading-relaxed group-hover:text-white/60 transition-colors duration-300">{principle.description}</p>
    </div>
  );
}

function StudioFacts() {
  const ref = useScrollReveal();
  const facts = [
    { number: '2002', label: 'Year Founded', desc: 'Over two decades of residential architecture expertise.' },
    { number: 'NYC +\nWestchester', label: 'Service Region', desc: 'Serving clients across the greater New York metro area.' },
    { number: '4', label: 'Core Services', desc: 'Custom homes, renovations, additions, and spatial planning.' },
    { number: '5.0', label: 'Client Rating', desc: 'Consistent 5-star client satisfaction across all projects.' },
  ];

  return (
    <div className="py-36 px-8 lg:px-16 bg-parchment">
      <div className="max-w-7xl mx-auto">
        <div ref={ref} className="reveal-on-scroll grid grid-cols-2 lg:grid-cols-4 gap-0 border-l border-t border-stone">
          {facts.map((f, i) => (
            <div key={i} className="p-10 border-r border-b border-stone group hover:bg-soft-white transition-colors duration-300 cursor-default">
              <p className="font-serif text-4xl font-light text-dark-ink mb-2 whitespace-pre-line leading-tight">{f.number}</p>
              <p className="font-sans text-[10px] tracking-[0.18em] uppercase text-bronze mb-3">{f.label}</p>
              <p className="font-sans text-xs text-warm-gray leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
